import React, { useState, useEffect } from "react";
import { useLang } from "../contexts/LanguageContext";
import { Menu, X } from "lucide-react";
import ServiceBadge247 from "./ServiceBadge247";

const DANA_AVATAR = "https://customer-assets.emergentagent.com/wingman/4d88747a-9285-4415-a858-393ef890ce1a/attachments/15204f80bb5a4f1087b4962c549d4d64_image.bin";

const BRAND_LOGO = "/tripex-logo-white.png";

export default function Navbar({ onCtaClick }) {
  const { t, toggle, lang, isAr } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  // Desktop nav items (full list)
  const navItems = [
    { key: "nav_about", href: "#about-tripex" },
    { key: "nav_cities", href: "#cities" },
    { key: "nav_dana", href: "#dana" },
  ];

  // Mobile nav items — Cities re-included, Dana with avatar
  const mobileItems = [
    { key: "nav_about", labelKey: "nav_about", href: "#about-tripex", avatar: null },
    { key: "nav_cities", labelKey: "nav_cities", href: "#cities", avatar: null },
    { key: "nav_dana_full", labelKey: "nav_dana_full", href: "#dana", avatar: DANA_AVATAR },
  ];

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "py-3" : "py-5"
      }`}
    >
      {/* Persistent glassmorphism — constant blur from top to bottom */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'rgba(250, 248, 240, 0.45)',
          backdropFilter: 'blur(16px) saturate(180%)',
          WebkitBackdropFilter: 'blur(16px) saturate(180%)',
          borderBottom: '1px solid rgba(15, 36, 33, 0.05)',
        }}
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-7xl px-5">
        <div className={`rounded-full flex items-center justify-between ps-3 pe-2 py-2`}>
          {/* Tripex brand logo + smart concierge tagline */}
          <div className="flex items-center ps-2 gap-3 md:gap-4">
            <a href="#top" className="flex items-center group relative" data-testid="navbar-logo" aria-label="Tripex">
              {/* Colored logo (T colored + dark Tripex) — consistent across the site */}
              <img
                src="/tripex-logo.png"
                alt="Tripex"
                className="brand-logo-img relative z-10"
                style={{
                  filter: 'drop-shadow(0 1px 6px rgba(15,36,33,0.15))',
                }}
                draggable="false"
              />
            </a>
            {/* Vertical divider */}
            <span
              className="hidden sm:inline-block h-7 w-px"
              style={{
                background: 'linear-gradient(to bottom, transparent 0%, rgba(15,36,33,0.3) 50%, transparent 100%)',
              }}
              aria-hidden="true"
            />
            {/* Smart tagline */}
            <span
              className="hidden sm:inline-block font-black whitespace-nowrap"
              data-testid="navbar-tagline"
              style={{
                fontFamily: isAr ? 'Cairo' : "'Montserrat', 'Plus Jakarta Sans', sans-serif",
                color: '#7C4DBE',
                fontSize: isAr ? '1.05rem' : '0.95rem',
                letterSpacing: isAr ? '0' : '-0.01em',
                fontWeight: 900,
              }}
            >
              {t("hero_kicker")}
            </span>
          </div>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navItems.map((it) => (
              <a
                key={it.key}
                href={it.href}
                onClick={(e) => {
                  if (it.href === "#dana") {
                    e.preventDefault();
                    window.dispatchEvent(new Event('tripex:open-dana'));
                  }
                }}
                data-testid={`navlink-${it.key}`}
                className="link-uline font-extrabold tracking-wide transition-all duration-300 hover:opacity-80"
                style={{
                  fontFamily: isAr ? 'Cairo' : 'Plus Jakarta Sans',
                  fontSize: isAr ? '1.25rem' : '1.0625rem',
                  color: '#7C4DBE',
                  letterSpacing: isAr ? '0' : '0.01em',
                }}
              >
                {t(it.key)}
              </a>
            ))}
          </nav>

          {/* Right cluster */}
          <div className="flex items-center gap-2.5">
            <ServiceBadge247
              variant="solid"
              size={40}
              className="hidden sm:inline-flex"
              title={isAr ? "خدمة على مدار الساعة" : "24/7 service"}
            />
            <button
              onClick={toggle}
              data-testid="lang-toggle-btn"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full border-2 border-emerald/60 bg-cream-light/60 px-3.5 py-1.5 text-xs font-bold text-emerald-deep hover:bg-emerald hover:text-cream hover:border-emerald transition-all duration-300"
            >
              <span className="font-display-en">{lang === "ar" ? "EN" : "عربي"}</span>
            </button>
            <button
              onClick={onCtaClick}
              data-testid="navbar-cta"
              className="btn-primary !py-2 !px-5 text-sm hidden sm:inline-flex"
              style={{ fontFamily: isAr ? 'Cairo' : 'Plus Jakarta Sans' }}
            >
              {t("nav_start")}
            </button>
            <button
              onClick={() => setOpen(!open)}
              data-testid="navbar-mobile-toggle"
              className="md:hidden p-2 rounded-full hover:bg-sage/20 transition"
              aria-label="menu"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile menu — dark glassmorphism frame for legibility */}
        {open && (
          <div
            className="md:hidden mt-3 rounded-3xl p-6 animate-fade-up shadow-2xl"
            data-testid="mobile-menu"
            style={{
              background: 'linear-gradient(160deg, rgba(15,36,33,0.96) 0%, rgba(15,36,33,0.92) 100%)',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
              border: '1px solid rgba(245,239,224,0.12)',
            }}
          >
            <div className="flex flex-col gap-1 text-center">
              {mobileItems.map((it) => (
                <a
                  key={it.key}
                  href={it.href}
                  onClick={(e) => {
                    if (it.href === "#dana") {
                      e.preventDefault();
                      window.dispatchEvent(new Event('tripex:open-dana'));
                    }
                    setOpen(false);
                  }}
                  data-testid={`mobile-nav-${it.key}`}
                  className="flex items-center justify-center gap-3 font-extrabold py-3 rounded-xl hover:bg-white/5 transition"
                  style={{
                    fontFamily: isAr ? 'Cairo' : 'Plus Jakarta Sans',
                    fontSize: isAr ? '1.5rem' : '1.25rem',
                    color: '#C499E8',
                  }}
                >
                  {it.avatar && (
                    <img
                      src={it.avatar}
                      alt=""
                      className="w-9 h-9 rounded-full object-cover border-2 border-cream/30 shadow-md"
                      draggable="false"
                    />
                  )}
                  <span>{t(it.labelKey)}</span>
                </a>
              ))}
              <div className="h-px bg-cream/10 my-3" />
              <div className="flex items-center justify-center gap-3 pt-1">
                <button
                  onClick={() => { toggle(); setOpen(false); }}
                  data-testid="lang-toggle-mobile"
                  className="rounded-full border border-cream/30 px-4 py-2 text-xs font-bold text-cream hover:bg-cream/10 transition"
                >
                  {lang === "ar" ? "EN" : "عربي"}
                </button>
                <button
                  onClick={() => { setOpen(false); onCtaClick && onCtaClick(); }}
                  className="rounded-full bg-cream text-emerald-deep px-5 py-2.5 text-sm font-bold hover:bg-gold transition"
                  style={{ fontFamily: isAr ? 'Cairo' : 'Plus Jakarta Sans' }}
                >
                  {t("nav_start")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
